// Registry of the data files the engine may read, with a driver that
// does the actual DuckDB-wasm calls. Import-free so the node harness
// can exercise the apply and re-apply logic with a fake driver.
//
// Two shapes of entry:
//   - handle entries (local folders): registered as a browser File,
//     read lazily by DuckDB. Cheap, so every one is applied on the
//     first run.
//   - buffer entries (cloud): the bytes must be produced first, by a
//     download and a decrypt, so they are applied on demand through
//     `ensureLoaded`.
//
// Registrations die with the DuckDB instance. The driver reports a
// session id that changes on every reset; a registration made under an
// older id counts as gone.

export interface DataDriver {
  registerHandle(name: string, file: File): Promise<void>;
  registerBuffer(name: string, bytes: Uint8Array): Promise<void>;
  drop(names: string[]): Promise<void>;
  sessionId(): number;
}

export interface DataEntryHandle {
  path: string;
  kind: 'handle';
  file(): Promise<File>;
}

export interface DataEntryBuffer {
  path: string;
  kind: 'buffer';
  bytes(): Promise<Uint8Array>;
}

export type DataEntry = DataEntryHandle | DataEntryBuffer;

export class DataRegistry {
  private entries = new Map<string, DataEntry>();
  /// path → session id the registration was made under.
  private registered = new Map<string, number>();
  /// path → size and modified time of the File registered for it.
  private stamps = new Map<string, { size: number; mtime: number }>();
  private listeners = new Set<() => void>();
  /// Calls run one after another. Two clicks, or a click during a run,
  /// never register the same name twice. A replaceAll waits its turn,
  /// so it never drops a file that a load still in flight is using.
  private tail: Promise<unknown> = Promise.resolve();

  constructor(private driver: DataDriver) {}

  /// Replace the whole set, because a new folder or environment was
  /// loaded. Names registered from the old set are dropped from DuckDB.
  /// Routed through `tail`, like `ensureLoaded`, so it waits its turn
  /// instead of racing a load that is still in flight.
  async replaceAll(entries: DataEntry[]): Promise<void> {
    const run = this.tail.then(
      () => this.applyReplace(entries),
      () => this.applyReplace(entries),
    );
    this.tail = run.catch(() => undefined);
    return run;
  }

  private async applyReplace(entries: DataEntry[]): Promise<void> {
    const old = [...this.registered.keys()];
    this.entries = new Map(entries.map((e) => [e.path, e]));
    this.registered.clear();
    this.stamps.clear();
    if (old.length > 0) {
      try {
        await this.driver.drop(old);
      } catch (err) {
        // The session may be gone already; nothing to drop then.
        console.warn('[kolilang] dropping stale data registrations failed', err);
      }
    }
    this.notify();
  }

  paths(): string[] {
    return [...this.entries.keys()];
  }

  isLoaded(path: string): boolean {
    return this.registered.get(path) === this.driver.sessionId();
  }

  /// Make every handle entry plus the listed buffer entries available in
  /// the current session. Returns the paths that were registered now.
  async ensureLoaded(bufferPaths: readonly string[]): Promise<string[]> {
    const run = this.tail.then(
      () => this.applyLoaded(bufferPaths),
      () => this.applyLoaded(bufferPaths),
    );
    this.tail = run.catch(() => undefined);
    return run;
  }

  /// Does the actual registering. `ensureLoaded` only calls this
  /// through the `tail` chain, one call at a time.
  /// A listed handle entry may already be registered. Then its File is
  /// read again and compared to the stamp from registration. A changed
  /// size or mtime means the file was edited on disk, so it is dropped
  /// and registered again.
  /// Each entry has its own try/catch. One unreadable file does not
  /// stop the rest. Every failure is named in one error raised at the
  /// end.
  private async applyLoaded(bufferPaths: readonly string[]): Promise<string[]> {
    const sid = this.driver.sessionId();
    const done: string[] = [];
    const failures: string[] = [];
    for (const e of this.entries.values()) {
      const listed = bufferPaths.includes(e.path);
      if (e.kind !== 'handle' && !listed) continue;
      try {
        if (this.registered.get(e.path) === sid) {
          if (e.kind !== 'handle' || !listed) continue;
          const file = await e.file();
          const stamp = this.stamps.get(e.path);
          if (stamp && stamp.size === file.size && stamp.mtime === file.lastModified) continue;
          await this.driver.drop([e.path]);
          // The drop already succeeded, so DuckDB has lost this path.
          // Clear our own record of it now. If the next line fails, the
          // path stays correctly marked as not loaded.
          this.registered.delete(e.path);
          this.stamps.delete(e.path);
          await this.driver.registerHandle(e.path, file);
          this.registered.set(e.path, sid);
          this.stamps.set(e.path, { size: file.size, mtime: file.lastModified });
          done.push(e.path);
          continue;
        }
        if (e.kind === 'handle') {
          const file = await e.file();
          await this.driver.registerHandle(e.path, file);
          this.stamps.set(e.path, { size: file.size, mtime: file.lastModified });
        } else {
          await this.driver.registerBuffer(e.path, await e.bytes());
        }
        this.registered.set(e.path, sid);
        done.push(e.path);
      } catch (err) {
        failures.push(`${e.path} (${err instanceof Error ? err.message : String(err)})`);
      }
    }
    if (done.length > 0) this.notify();
    if (failures.length > 0) throw new Error(`data files not loaded: ${failures.join(', ')}`);
    return done;
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }
}
